/**
 * The API reference, rendered from the OpenAPI document the app generates.
 *
 * ONE COMPONENT, TWO AUDIENCES, TWO ENDPOINTS. Staff get
 * /api/v1/developer-tools/openapi.json — every route in the build. A club
 * gets /api/v1/api-reference — the routes their own token can actually call.
 * The difference is the endpoint, not the rendering: documenting a club's
 * surface differently from ours is how the two drift.
 *
 * NOTHING HERE IS HAND-WRITTEN. The document is generated from Flask's
 * url_map with each guard read off the view's own decorators, so this cannot
 * describe a route that no longer exists or claim a guard that was removed.
 * That includes the expanded detail: an operation with no documented
 * parameters shows none, rather than inventing a schema.
 */
import { useEffect, useMemo, useState } from 'react'

import { ApiError, api, API_BASE } from '../../lib/api'
import './api-reference.css'

type Schema = {
  type?: string
  description?: string
}
type Parameter = {
  name: string
  in: string
  required?: boolean
  description?: string
  schema?: Schema
}
type Operation = {
  summary?: string
  description?: string
  tags?: string[]
  parameters?: Parameter[]
  requestBody?: {
    content?: Record<string, { schema?: {
      properties?: Record<string, Schema>
      required?: string[]
    } }>
  }
  responses?: Record<string, { description?: string }>
  security?: Record<string, string[]>[]
  'x-guard'?: string
}
type Doc = {
  info?: { title?: string; version?: string }
  paths: Record<string, Record<string, Operation>>
}

const METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const

function ParamRows({ params }: { params: Parameter[] }) {
  return (
    <>
      {params.map((p) => (
        <tr key={`${p.in} ${p.name}`}>
          <td className="mono">{p.name}</td>
          <td className="muted">{p.schema?.type ?? 'string'}</td>
          <td className="muted">{p.required ? 'required' : 'optional'}</td>
          <td className="muted">{p.description || '—'}</td>
        </tr>
      ))}
    </>
  )
}

function OperationDetails({ op }: { op: Operation }) {
  const pathParams = (op.parameters ?? []).filter((p) => p.in === 'path')
  const queryParams = (op.parameters ?? []).filter((p) => p.in === 'query')

  // The request body, when the document declares one: a single content type
  // with an object schema, generated by app/helpers/openapi_export.py.
  const bodyEntry = Object.entries(op.requestBody?.content ?? {})[0]
  const bodyContentType = bodyEntry?.[0]
  const bodyProperties = Object.entries(bodyEntry?.[1]?.schema?.properties ?? {})
  const bodyRequired = new Set(bodyEntry?.[1]?.schema?.required ?? [])

  const responses = Object.entries(op.responses ?? {})
    .sort(([a], [b]) => a.localeCompare(b))

  const hasParams = pathParams.length > 0 || queryParams.length > 0

  return (
    <div className="apiref__opdetail">
      {op.description && (
        <p className="apiref__opdesc">{op.description}</p>
      )}

      {op['x-guard'] && (
        <p className="muted apiref__guard">
          <span className="apiref__label">Authorization</span> {op['x-guard']}
        </p>
      )}

      {hasParams && (
        <div className="apiref__section">
          <h4 className="apiref__label">Parameters</h4>
          <table className="apiref__subtable">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Required</th><th>Description</th></tr>
            </thead>
            <tbody>
              <ParamRows params={pathParams} />
              <ParamRows params={queryParams} />
            </tbody>
          </table>
        </div>
      )}

      {bodyProperties.length > 0 && (
        <div className="apiref__section">
          <h4 className="apiref__label">
            Request body{bodyContentType ? ` — ${bodyContentType}` : ''}
          </h4>
          <table className="apiref__subtable">
            <thead>
              <tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr>
            </thead>
            <tbody>
              {bodyProperties.map(([name, schema]) => (
                <tr key={name}>
                  <td className="mono">{name}</td>
                  <td className="muted">{schema.type ?? 'string'}</td>
                  <td className="muted">{bodyRequired.has(name) ? 'required' : 'optional'}</td>
                  <td className="muted">{schema.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {responses.length > 0 && (
        <div className="apiref__section">
          <h4 className="apiref__label">Responses</h4>
          <table className="apiref__subtable">
            <thead>
              <tr><th style={{ width: '5rem' }}>Status</th><th>Description</th></tr>
            </thead>
            <tbody>
              {responses.map(([status, r]) => (
                <tr key={status}>
                  <td className="mono">{status}</td>
                  <td className="muted apiref__respdesc">{r.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!op.description && !hasParams && bodyProperties.length === 0
        && responses.length === 0 && (
        <p className="muted">
          Nothing further is documented for this endpoint yet.
        </p>
      )}
    </div>
  )
}

export default function ApiReference({ scope }: { scope: 'club' | 'platform' }) {
  const endpoint = scope === 'club'
    ? '/api/v1/api-reference'
    : '/api/v1/developer-tools/openapi.json'

  const [doc, setDoc] = useState<Doc | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  useEffect(() => {
    setDoc(null)
    setError(null)
    setExpanded(new Set())
    api<Doc>(endpoint)
      .then(setDoc)
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
  }, [endpoint])

  const rows = useMemo(() => {
    if (!doc?.paths) return []
    const out: { path: string; method: string; op: Operation }[] = []
    for (const [path, item] of Object.entries(doc.paths)) {
      for (const method of METHODS) {
        const op = item[method]
        if (op) out.push({ path, method, op })
      }
    }
    const q = filter.trim().toLowerCase()
    return out
      .filter((r) => !q || r.path.toLowerCase().includes(q)
        || (r.op.summary ?? '').toLowerCase().includes(q))
      // Alphabetical by path: the question is "is this endpoint here", and
      // whatever order the url_map happens to yield does not answer it.
      .sort((a, b) => a.path.localeCompare(b.path) || a.method.localeCompare(b.method))
  }, [doc, filter])

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (error) return <div className="panel error">Could not load the reference — {error}</div>
  if (!doc) return <p className="muted">Loading the reference…</p>

  return (
    <section className="apiref">
      <p className="muted apiref__note">
        {scope === 'club'
          ? 'The endpoints an API key from this club can call. Generated from '
            + 'the running build, so it describes what this environment '
            + 'actually serves rather than what was written down.'
          : 'Every route in this build, with the guard read off each view’s '
            + 'own decorators.'}
        {' '}
        <a href={`${API_BASE}${endpoint}`} target="_blank" rel="noreferrer">
          Download the OpenAPI document
        </a>
        {' — importable into Postman or an SDK generator.'}
      </p>

      <input className="adm-input apiref__filter" value={filter}
             onChange={(e) => setFilter(e.target.value)}
             placeholder="Filter by path or summary" />

      <p className="muted apiref__count">
        {rows.length} endpoint{rows.length === 1 ? '' : 's'}
        {filter.trim() && ' matching'}
      </p>

      <table className="apiref__table">
        <thead>
          <tr>
            <th style={{ width: '5rem' }}>Method</th>
            <th>Path</th>
            <th>What it does</th>
            <th style={{ width: '2rem' }}>
              <span className="visually-hidden">Details</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = `${r.method} ${r.path}`
            const isOpen = expanded.has(key)
            return (
              <FragmentRow key={key} isOpen={isOpen} row={r}
                           onToggle={() => toggle(key)} />
            )
          })}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="muted">Nothing matches that filter.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  )
}

function FragmentRow({ row, isOpen, onToggle }: {
  row: { path: string; method: string; op: Operation }
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr className={`apiref__row${isOpen ? ' apiref__row--open' : ''}`}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onToggle()
            }
          }}
          tabIndex={0}
          role="button"
          aria-expanded={isOpen}>
        <td><span className={`apiref__verb apiref__verb--${row.method}`}>
          {row.method.toUpperCase()}
        </span></td>
        <td className="mono">{row.path}</td>
        <td className="muted">{row.op.summary ?? '—'}</td>
        <td className="apiref__chevroncell">
          <span className="apiref__chevron" aria-hidden="true">
            {isOpen ? '▾' : '▸'}
          </span>
        </td>
      </tr>
      {isOpen && (
        <tr className="apiref__detailrow">
          <td colSpan={4}>
            <OperationDetails op={row.op} />
          </td>
        </tr>
      )}
    </>
  )
}
